import React from 'react'

import cn from 'classnames'

import styles from './FloatingLabelInput.module.scss'
import Root from './Root'

// Placeholders are required to make floating labels work.
// This will be the default placeholder text if none is given.
const NBSP = '\u00A0'

interface InputEventsProps {
  onChange: (event: React.ChangeEvent | any) => void
  onBlur?: (event?: React.FocusEvent | any) => void
  onFocus?: (event?: React.FocusEvent | any) => void
}

interface InputProps {
  name: string
  // The input element to use. Can be a string or a React Component.
  // (default: 'input')
  inputElement?: any
  inputClass?: string
  type?: string
  // The greyed-out text to show when the input is empty.
  placeholder?: string
  // The value passed onto `<input>`.
  value?: string | number
  // Optional autoComplete
  autoComplete?: string

  // Boolean, true if the input should be disabled
  disabled?: boolean

  max?: string | number
  min?: string | number
  rows?: string | number
  cols?: string | number
  // Boolean, true if the input should be get focus onload
  autoFocus?: boolean
  'data-1p-ignore'?: boolean
}

interface LabelProps {
  // The label to be shown. If the input is empty, it'll be shown on top of the
  // field. When the input is focused or isn't empty, it'll be shown as part of
  // the border.
  //
  // No label will be shown if this is given as null, though this case isn't
  // handled completely (eg, some things may be unavailable, like the
  // 'required' option).
  label: string
  required?: boolean
  disabled?: boolean
}

interface PrefixProps {
  // Prefix to be shown (eg, `$`).
  prefix?: string
  prefixClass?: string
  disabled?: boolean
}

interface SuffixProps {
  // Suffix to be shown (eg, `.00`).
  suffix?: string
  suffixClass?: string
  disabled?: boolean
}

export interface FloatingLabelInputProps extends InputEventsProps, InputProps, LabelProps, PrefixProps, SuffixProps {
  inputRef?: string
  // Styles the control with a red border if set to `true`.
  invalid?: boolean
  touched?: boolean

  className?: string
  'data-testid'?: string
  variant?: {
    isSmall?: boolean
    isTiny?: boolean
  }
}

/**
 * Works a bit like `Form.Input`, but looks like Google/Shopify's input field
 * with "floating labels".
 */

const FloatingLabelInput = ({
  onChange,
  onBlur,
  name,
  label,
  invalid = false,
  touched = false,
  value,
  placeholder = NBSP,
  required,
  prefix,
  suffix,
  inputClass,
  prefixClass,
  suffixClass,
  variant,
  inputElement = 'input',
  autoComplete,
  autoFocus,
  disabled,
  'data-testid': dataTestId,
  type,
  min,
  max,
  rows,
  cols,
  'data-1p-ignore': data1pIgnore,
}: FloatingLabelInputProps) => {
  const inputId = name
  return (
    <Root
      data-testid={dataTestId || name}
      disabled={disabled}
      className={cn({
        [styles.root]: true,
        [styles.invalid]: invalid && touched,
        [styles.isSmall]: variant && variant.isSmall,
        [styles.isTiny]: variant && variant.isTiny,
      })}
    >
      {/* Prefixes (eg, '$') */}
      <Prefix {...{ prefix, prefixClass }} />

      {/* The actual input field */}
      <Input
        id={inputId}
        data-1p-ignore={data1pIgnore}
        {...{
          name,
          inputClass,
          inputElement,
          onChange,
          onBlur,
          placeholder,
          type,
          value,
          autoComplete,
          disabled,
          min,
          max,
          autoFocus,
          rows,
          cols,
        }}
      />

      {/* Suffixes (eg, '.00') */}
      <Suffix {...{ suffix, suffixClass }} />

      {/* The label */}
      <Label htmlFor={inputId} {...{ label, required }} />
    </Root>
  )
}

const Input = ({
  id,
  name,
  inputClass,
  inputElement: InputElement,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  type,
  value,
  autoComplete,
  autoFocus,
  disabled,
  min,
  max,
  rows,
  cols,
  'data-1p-ignore': data1pIgnore,
}: InputProps & InputEventsProps & { id: string }) => (
  <InputElement
    id={id}
    data-1p-ignore={data1pIgnore}
    className={cn(styles.input, inputClass)}
    name={name}
    type={type}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    onBlur={onBlur}
    onFocus={onFocus}
    autoComplete={autoComplete || 'off'}
    autoFocus={autoFocus}
    disabled={disabled}
    min={min}
    max={max}
    rows={rows}
    cols={cols}
  />
)

const Prefix = ({ prefix, prefixClass }: PrefixProps) =>
  prefix ? <span className={cn(styles.prefix, prefixClass, 'floatingLabelInputPrefix')}>{prefix}</span> : null

const Suffix = ({ suffix }: SuffixProps) =>
  suffix ? <span className={cn(styles.suffix, 'floatingLabelInputSuffix')}>{suffix}</span> : null

const Label = ({ label, required, htmlFor }: LabelProps & { htmlFor: string }) => {
  if (label == '') return null

  return (
    <label htmlFor={htmlFor} className={cn(styles.label, 'floatingLabelInputLabel')}>
      {label}
      {required ? <span className="requiredStar">*</span> : null}
    </label>
  )
}

export default FloatingLabelInput
